/**
 * Dismiss / snooze / restore actions for items in the scoped cosign queue.
 *
 * Mirrors the claims attention pattern (useClaimAttention): dismissal hides
 * the note from the queue and badge count but never waives the cosign
 * requirement — the note stays 'ready' and resurfaces if resubmitted.
 */

import { useCallback } from 'react';

import { useCosignDispositionMutation } from '../store/api';

export function useCosignDisposition() {
  const [dispositionMutation, { isLoading: isUpdating }] = useCosignDispositionMutation();

  const dismiss = useCallback(
    async (noteId: string) =>
      dispositionMutation({
        noteId,
        cosignDispositionRequest: { action: 'dismiss' },
      }).unwrap(),
    [dispositionMutation],
  );

  const remindTomorrow = useCallback(
    async (noteId: string) => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      return dispositionMutation({
        noteId,
        cosignDispositionRequest: {
          action: 'snooze',
          snooze_until: tomorrow.getTime() / 1000,
        },
      }).unwrap();
    },
    [dispositionMutation],
  );

  const restore = useCallback(
    async (noteId: string) =>
      dispositionMutation({
        noteId,
        cosignDispositionRequest: { action: 'clear' },
      }).unwrap(),
    [dispositionMutation],
  );

  return { dismiss, remindTomorrow, restore, isUpdating };
}
